import { MemberType } from '@lucidlink/dto/validation';
import { generateTraceId } from '@lucidlink/ts-crypto/crypto';
import { setRootLogger } from '@lucidlink/tscore';
import { createFetcher, defaultFetch } from '#fetcher';
import { createPersistentKvStorage } from '#kv-storage';
import { createSdkDefaultHeaders } from '#sdk/utils/create-sdk-default-headers';
import { AlreadyLoggedInError, NotLoggedInError, SessionManager, } from '#session/session-manager';
import { createAccountRecoveryClient } from './account-recovery-client';
import { createAuthenticationClient } from './authentication-client';
import { createCurrentUserManagement } from './current-user-management';
import { createGeneralFilespaceMigrationsClient } from './filespace-migrations-client';
import { createGuestClient } from './guest-client';
import { createPartnerOrganizationsClient } from './partner-organizations-client';
import { createPasswordResetClient } from './password-reset-client';
import { createPublicClient } from './public-client';
import { createTelemetryClient } from './telemetry-client';
import { createWorkspaceClient, } from './workspace-client';
import { createWorkspacesManagement } from './workspaces-management';
import { createZendeskClient } from './zendesk-client';
export const createSdk = async ({ baseUrl, kvStorage, fetch, logger, additionalInfo, }) => {
    if (!additionalInfo.application) {
        throw new Error('No application name provided');
    }
    const fetcher = createFetcher({
        baseUrl,
        baseUrlHasVersion: true,
        fetch: fetch ?? defaultFetch,
        traceIdGenerator: generateTraceId,
        defaultHeaders: createSdkDefaultHeaders(additionalInfo.application, additionalInfo.platform),
    });
    return new LucidSdkImpl(fetcher, logger, additionalInfo, kvStorage ?? (await createPersistentKvStorage()));
};
class LucidSdkImpl {
    fetcher;
    logger;
    additionalInfo;
    publicClientInstance;
    authenticationClientInstance;
    currentUserManagementInstance;
    workspacesManagementInstance;
    accountRecoveryClientInstance;
    passwordResetClientInstance;
    filespaceMigrationsClientInstance;
    globalTelemetryClient;
    globalFetcher;
    sessionManager;
    workspaceInstances = new Map();
    guestClientInstances = new Map();
    classicApiContextGetter;
    zendeskClientInstance;
    constructor(fetcher, logger, additionalInfo, kvStorage) {
        this.fetcher = fetcher;
        this.logger = logger;
        this.additionalInfo = additionalInfo;
        setRootLogger(this.logger);
        this.sessionManager = SessionManager.create({
            kvStorage,
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            onGlobalLogin: this.onUserLogin,
            onGlobalLogout: this.onUserLogout,
            onWorkspaceLogin: this.onWorkspaceLogin,
            onWorkspaceLogout: this.onWorkspaceLogout,
        });
        this.authenticationClientInstance = createAuthenticationClient({
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            getSessionManager: this.getSessionManager,
            getGlobalFetcher: this.getGlobalFetcher,
            logoutWorkspaces: this.logoutWorkspaces,
        });
        this.publicClientInstance = createPublicClient({
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
        });
        this.accountRecoveryClientInstance = createAccountRecoveryClient({
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
        });
        this.passwordResetClientInstance = createPasswordResetClient({
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
        });
        this.zendeskClientInstance = createZendeskClient({
            getUnauthenticatedFetcher: this.getUnauthenticatedFetcher,
            getGlobalFetcher: this.getGlobalFetcher,
        });
    }
    accountRecoveryClient = () => {
        if (this.currentUserManagementInstance) {
            throw new AlreadyLoggedInError();
        }
        return this.accountRecoveryClientInstance;
    };
    passwordResetClient = () => {
        if (this.currentUserManagementInstance) {
            throw new AlreadyLoggedInError();
        }
        return this.passwordResetClientInstance;
    };
    publicClient() {
        return this.publicClientInstance;
    }
    authenticationClient = () => this.authenticationClientInstance;
    currentUserManagement = () => {
        if (!this.currentUserManagementInstance) {
            throw new NotLoggedInError();
        }
        return this.currentUserManagementInstance;
    };
    workspacesManagement = () => {
        if (!this.workspacesManagementInstance) {
            throw new NotLoggedInError();
        }
        return this.workspacesManagementInstance;
    };
    workspaceClient = (workspaceId) => {
        if (!this.getGlobalFetcher()) {
            throw new NotLoggedInError();
        }
        let workspaceInstance = this.workspaceInstances.get(workspaceId);
        if (!workspaceInstance) {
            workspaceInstance = createWorkspaceClient({
                workspaceId,
                getSessionManager: this.getSessionManager,
                getGlobalFetcher: this.getGlobalFetcher,
                logger: this.logger,
                additionalInfo: this.additionalInfo,
                getGlobalTelemetryClient: this.telemetryClient,
                getClassicApiContext: () => this.callClassicApiContextGetterIfAvailable(),
            });
            this.workspaceInstances.set(workspaceId, workspaceInstance);
        }
        return workspaceInstance;
    };
    /**
     * @param {string} linkId - for now everything before the pound sign /g/1/<encryptedLink>#<invitationSecretKey>
     *                          later it will be `linkId` which will be uuid or base32/base64 encoded link
     */
    guestClient = (linkId) => {
        if (!this.getGlobalFetcher()) {
            throw new NotLoggedInError();
        }
        let guestClientInstance = this.guestClientInstances.get(linkId);
        if (!guestClientInstance) {
            guestClientInstance = createGuestClient({
                linkId,
                logger: this.logger,
                additionalInfo: this.additionalInfo,
                getSessionManager: this.getSessionManager,
                getGlobalFetcher: this.getGlobalFetcher,
                getGlobalTelemetryClient: this.telemetryClient,
            });
            this.guestClientInstances.set(linkId, guestClientInstance);
        }
        return guestClientInstance;
    };
    filespaceMigrationsClient = () => {
        if (!this.filespaceMigrationsClientInstance) {
            throw new NotLoggedInError();
        }
        return this.filespaceMigrationsClientInstance;
    };
    telemetryClient = () => {
        if (!this.globalTelemetryClient) {
            throw new NotLoggedInError();
        }
        return this.globalTelemetryClient;
    };
    zendeskClient = () => {
        if (!this.zendeskClientInstance) {
            throw new Error('Missing Zendesk Client');
        }
        return this.zendeskClientInstance;
    };
    partnerOrganizationsClient = () => {
        if (!this.getGlobalFetcher()) {
            throw new NotLoggedInError();
        }
        const partnerOrganizationsFetcher = this.getGlobalFetcher().createSubFetcher({
            path: 'partner-organizations',
        });
        return createPartnerOrganizationsClient({
            partnerOrganizationsFetcher,
        });
    };
    setClassicApiContextGetter(getter) {
        this.classicApiContextGetter = getter;
    }
    callClassicApiContextGetterIfAvailable() {
        if (!this.classicApiContextGetter) {
            return Promise.resolve(undefined);
        }
        return this.classicApiContextGetter();
    }
    getUnauthenticatedFetcher = () => this.fetcher;
    getSessionManager = () => this.sessionManager;
    getGlobalFetcher = () => {
        if (!this.globalFetcher) {
            throw new NotLoggedInError();
        }
        return this.globalFetcher;
    };
    logoutWorkspaces = async () => {
        // Tear down both account workspace clients and guest clients so a guest
        // logout clears all sessions. `allSettled` ensures one failing client
        // logout can't abort the chain before `sessionManager.logout()` runs.
        await Promise.allSettled([
            ...Array.from(this.workspaceInstances.values()).map((workspaceClient) => workspaceClient.logout()),
            ...Array.from(this.guestClientInstances.values()).map((guestClient) => guestClient.logout()),
        ]);
    };
    onUserLogin = async () => {
        this.globalFetcher = this.fetcher.createSubFetcher({
            getAccessToken: (forceRefresh) => this.sessionManager.getFreshGlobalToken(Boolean(forceRefresh)),
        });
        const accountDetails = await this.sessionManager.getAccountDetails();
        const commonData = accountDetails.type === MemberType.USER
            ? { userId: accountDetails.userId }
            : { serviceAccountId: accountDetails.serviceAccountId };
        this.globalTelemetryClient = createTelemetryClient({
            logger: this.logger,
            getFetcher: this.getGlobalFetcher,
            additionalInfo: this.additionalInfo,
            commonData,
        });
        this.currentUserManagementInstance = createCurrentUserManagement({
            getAuthenticatedFetcher: this.getGlobalFetcher,
            getAccountDetails: () => this.sessionManager.getAccountDetails(),
            getSessionManager: () => this.sessionManager,
            getTelemetryClient: this.telemetryClient,
        });
        this.workspacesManagementInstance = createWorkspacesManagement({
            getGlobalFetcher: this.getGlobalFetcher,
            getWorkspaceFetcher: (workspaceId) => {
                return this.workspaceClient(workspaceId).getWorkspaceFetcher();
            },
            getSessionManager: this.getSessionManager,
            loginToWorkspace: (workspaceId) => this.workspaceClient(workspaceId).login(),
        });
        this.filespaceMigrationsClientInstance =
            createGeneralFilespaceMigrationsClient({
                getGlobalFetcher: this.getGlobalFetcher,
                getClassicApiContext: () => this.callClassicApiContextGetterIfAvailable(),
                getSessionManager: () => this.sessionManager,
            });
    };
    onUserLogout = () => {
        this.globalFetcher = undefined;
        this.currentUserManagementInstance = undefined;
        this.workspacesManagementInstance = undefined;
        this.filespaceMigrationsClientInstance = undefined;
        this.globalTelemetryClient = undefined;
        this.workspaceInstances.clear();
        this.guestClientInstances.clear();
    };
    onWorkspaceLogin = (_workspaceId) => {
        return;
    };
    onWorkspaceLogout = (workspaceId) => {
        this.workspaceInstances.delete(workspaceId);
    };
}
